import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { ProductsQueryDtoSortOrderEnum } from "../../../api";

interface UsePaginationSortingUrlSyncOptions {
    defaultPageSize?: number;
}

export function usePaginationSortingUrlSync({
    defaultPageSize = 10,
}: UsePaginationSortingUrlSyncOptions = {}) {
    const [ searchParams, setSearchParams ] = useSearchParams();

    // Initialize pagination from URL
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    const urlpageSize = parseInt(
        searchParams.get("pageSize") || String(defaultPageSize),
        10
    );
    const [ pagination, setPagination ] = useState<PaginationState>({
        pageIndex: urlPage - 1, // Convert 1-based to 0-based
        pageSize: urlpageSize,
    });

    // Initialize sorting from URL
    const urlSortBy = searchParams.get("sortBy");
    const urlSortOrder = searchParams.get("sortOrder");
    const initialSorting: SortingState = useMemo(() => {
        if (urlSortBy) {
            return [
                {
                    id: urlSortBy,
                    desc: urlSortOrder === ProductsQueryDtoSortOrderEnum.Desc,
                },
            ];
        }
        return [];
    }, [ urlSortBy, urlSortOrder ]);

    const [ sorting, setSorting ] = useState<SortingState>(initialSorting);

    // Update URL when pagination changes
    useEffect(() => {
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                params.set("page", String(pagination.pageIndex + 1)); // Convert 0-based to 1-based
                params.set("pageSize", String(pagination.pageSize));
                return params;
            },
            { replace: true }
        );
    }, [ pagination, setSearchParams ]);

    // Update URL when sorting changes
    useEffect(() => {
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                if (sorting.length > 0) {
                    const firstSort = sorting[ 0 ];
                    params.set("sortBy", firstSort.id);
                    params.set(
                        "sortOrder",
                        firstSort.desc ? ProductsQueryDtoSortOrderEnum.Desc : ProductsQueryDtoSortOrderEnum.Asc
                    );
                    // Reset to page 1 when sorting changes
                    params.set("page", "1");
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                } else {
                    params.delete("sortBy");
                    params.delete("sortOrder");
                }
                return params;
            },
            { replace: true }
        );
    }, [ sorting, setSearchParams ]);

    // Convert sorting state to query parameters
    const sortingQuery = useMemo(() => {
        if (sorting.length === 0) return {};
        const firstSort = sorting[ 0 ];
        return {
            sortBy: firstSort.id,
            sortOrder: firstSort.desc ? ProductsQueryDtoSortOrderEnum.Desc : ProductsQueryDtoSortOrderEnum.Asc,
        };
    }, [ sorting ]);

    return {
        pagination,
        setPagination,
        sorting,
        setSorting,
        sortingQuery,
    };
}

